/**
 * Extracts all default values from component metadata.
 * Creates an object mapping argument names to their default values.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.meta - Metadata object containing argument definitions
 * @returns {Object} Object with argument names as keys and default values as values
 *
 * @example
 * const defaults = getAllDefaultValuesFromMetadata({
 *   meta: {
 *     size: { type: STRING, defaultValue: 'medium' },
 *     visible: { type: BOOLEAN, defaultValue: true }
 *   }
 * });
 * // Returns: { size: 'medium', visible: true }
 */
export function getAllDefaultValuesFromMetadata({ meta }) {
  const defaults = {};

  getArgumentNamesFromMetadata({ meta }).forEach(key => {
    defaults[key] = meta[key].defaultValue;
  });

  return defaults;
}

/**
 * Extracts all argument names from component metadata.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.meta - Metadata object containing argument definitions
 * @returns {Array<string>} Array of argument names
 *
 * @example
 * const names = getArgumentNamesFromMetadata({
 *   meta: { size: {...}, visible: {...} }
 * });
 * // Returns: ['size', 'visible']
 */
export function getArgumentNamesFromMetadata({ meta }) {
  if (!meta) return [];

  return Object.keys(meta);
}

/**
 * Gets the default value for a specific argument from component metadata.
 *
 * @param {Object} metadata - Metadata object with meta property
 * @param {string} argument - Name of the argument to get default value for
 * @returns {*} The default value for the specified argument
 *
 * @example
 * const defaultSize = getDefaultValueFromMetadata(METADATA, 'size');
 * // Returns: 'medium'
 */
export function getDefaultValueFromMetadata(metadata, argument) {
  return getAllDefaultValuesFromMetadata(metadata)[argument];
}

/**
 * Marks all arguments in a metadata object as private.
 * Private arguments are not exposed in the component's public API
 * and are used for internal implementation details.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.meta - Metadata object containing argument definitions
 * @returns {Object} New metadata object with all arguments marked as private
 *
 * @example
 * const privateArgs = makeArgumentPrivate({
 *   meta: {
 *     internalFlag: { type: BOOLEAN, defaultValue: false }
 *   }
 * });
 * // Returns: { internalFlag: { type: BOOLEAN, defaultValue: false, private: true } }
 */
export function makeArgumentsPrivate({ meta }) {
  let result = {};

  Object.entries(meta).forEach(([key, metaItem]) => {
    result[key] = { ...metaItem, private: true };
  });

  return { meta: result };
}

/**
 * Helper to pick specific args from a shared config
 *
 * @param {Object} sharedMetadata - Source config object
 * @param {Array<string>} argNames - Prop names to include
 * @returns {Object} Metadata with only specified args
 *
 * @example
 * const gapOnly = pickArgs(sharedProps.flex, ['gap']);
 */
export function pickArgs(sharedMetadata, argNames) {
  const picked = { meta: {}, defaults: {} };

  argNames.forEach(name => {
    if (sharedMetadata.meta?.[name]) {
      picked.meta[name] = sharedMetadata.meta[name];
    }
  });

  return picked;
}

/**
 * Prepares metadata for documentation by filtering out private arguments.
 * Only includes arguments that are part of the public API.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.meta - Metadata object containing argument definitions
 * @returns {Object} Metadata object with only public arguments
 *
 * @example
 * const docsData = setupDocumentationData({
 *   meta: {
 *     size: { type: STRING },
 *     _internal: { type: BOOLEAN, private: true }
 *   }
 * });
 * // Returns: { size: { type: STRING } }
 */
export function setupDocumentationData({ meta }) {
  const argsForDocs = {};

  getArgumentNamesFromMetadata({ meta }).forEach(key => {
    if (meta[key].private) return;

    argsForDocs[key] = meta[key];
  });

  return argsForDocs;
}

/**
 * Sorts metadata arguments alphabetically by key.
 * Useful for consistent ordering in component metadata definitions.
 *
 * @param {Object} metadata - Metadata object with meta property
 * @returns {Object} Metadata object with alphabetically sorted keys
 *
 * @example
 * const sorted = sortMetadata({
 *   meta: { zIndex: {...}, background: {...}, padding: {...} }
 * });
 * // Returns: { meta: { background: {...}, padding: {...}, zIndex: {...} } }
 */
export function sortMetadata(metadata) {
  if (!metadata || !metadata.meta) {
    return metadata;
  }

  const sortedKeys = Object.keys(metadata.meta).sort();

  const sortedMeta = {};
  sortedKeys.forEach(key => {
    sortedMeta[key] = metadata.meta[key];
  });

  return {
    ...metadata,
    meta: sortedMeta,
  };
}

export default {
  getAllDefaultValuesFromMetadata,
  getArgumentNamesFromMetadata,
  getDefaultValueFromMetadata,
  makeArgumentsPrivate,
  pickArgs,
  setupDocumentationData,
  sortMetadata,
};
